// :cow: Cosmose CONFIDENTIAL :iso:
import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { ConfigService } from '@med/app/core/config/config.service';
import { LanguageService } from '@med/app/core/language/service/language.service';
import { concatMap } from 'rxjs/operators';
import { PlannerBasicDemoDataProviderService } from '@med/app/home/planner-basic-demo/planner-basic-demo-data-provider.service';
import { PlannerBasicDemoService } from '@med/app/home/planner-basic-demo/planner-basic-demo.service';

@Injectable({providedIn: 'root'})
export class PlannerBasicDemoInterceptor implements HttpInterceptor {

  constructor(
    private jpDemoDataProviderService: PlannerBasicDemoDataProviderService,
    private configService: ConfigService,
    private languageService: LanguageService,
    private plannerBasicDemoService: PlannerBasicDemoService,
  ) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.plannerBasicDemoService.isDemoEnabled()) {
      return next.handle(req);
    }

    if (!req.url.includes(this.configService.getConfig().apiUrl)) {
      return next.handle(req);
    }

    return this.getBody(req.url, req.method).pipe(
      concatMap(body => {
        if (body) {
          const response = new HttpResponse({status: 200, body: body});

          return of(response);
        }

        return next.handle(req);
      }),
    );
  }

  private getBody(url: string, method: string): Observable<any> {
    const stepId = this.plannerBasicDemoService.getStepId();

    return this.languageService.getLanguage().pipe(
      concatMap(language => {
        switch (true) {
          case (method === 'GET' && url.endsWith('/platforms/all-available')):
            return this.jpDemoDataProviderService.getPlatforms(language);

          case (method === 'GET' && url.includes('/ad-formats')):
            return this.jpDemoDataProviderService.getAdformats(language);

          case url.endsWith('/campaigns/demo'):
          case url.endsWith('/campaigns/demo/audience-segments/business-categories'):
          case url.endsWith('/campaigns/demo/audience-segments/cities'):
          case method === 'PUT' && url.endsWith('/campaigns/demo/platforms'):
          case method === 'PUT' && url.endsWith('/campaigns/demo/ad-formats'):
            return this.jpDemoDataProviderService.getCampaign(stepId, language);

          case url.includes('/competitor-brands'):
            return this.jpDemoDataProviderService.getCompetitorBrands(language);
          case url.includes('/location-unit-groups'):
            return this.jpDemoDataProviderService.getLocationUnitGroups(language);
          case url.includes('/estimates'):
            return this.jpDemoDataProviderService.getEstimates(stepId);
          case url.includes('/demographics'):
            return this.jpDemoDataProviderService.getDemographics(language);
          case url.includes('/campaign-presets'):
            return this.jpDemoDataProviderService.getCampaignPresets(language);
          case url.includes('/campaign-defaults'):
            return this.jpDemoDataProviderService.getCampaignDefaults();
          case url.includes('/countries'):
            return this.jpDemoDataProviderService.getCountries(language);
          case url.includes('/asset-packages'):
            return this.jpDemoDataProviderService.getRecommendedPlatforms(language);
          case url.includes('/cities'):
            return this.jpDemoDataProviderService.getCities(language);
          case url.includes('/spot-plan'):
            return this.jpDemoDataProviderService.getSpotplan();
          case url.includes('/budget'):
            return this.jpDemoDataProviderService.getBudget();
          case url.includes('/publish'):
            return this.jpDemoDataProviderService.getCampaign(stepId, language);
          case url.includes('/clients'):
            return this.jpDemoDataProviderService.getClients();
          case url.endsWith('/campaigns'):
            return this.jpDemoDataProviderService.getCampaignList(stepId, language);
          default:
            throw new Error(`No provider defined for url: ${ url }`);
        }
      }),
    );

  }
}
